import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') || '';
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Missing reset token');
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPass.length < 6) return toast.error('Password must be at least 6 characters');
    if (newPass !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: newPass });
      setDone(true);
      toast.success('Password reset — you can now log in.');
      setTimeout(() => nav('/login'), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <img src="/brand/logo-icon@2x.png" alt="PTT NZ" className="w-10 h-10 rounded-lg" />
          <span className="font-display font-black text-xl">PTT NZ Alerts</span>
        </div>

        {!token ? (
          <div className="card bg-red-50 border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold text-red-900">Invalid reset link</div>
                <p className="text-sm text-red-800 mt-1">This link is missing a token. Please request a new password reset link.</p>
              </div>
            </div>
            <div className="mt-4"><Link to="/forgot-password" className="btn btn-primary">Request new link</Link></div>
          </div>
        ) : done ? (
          <div className="card bg-teal-50 border-teal-200">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold text-teal-900">Password reset</div>
                <p className="text-sm text-teal-800 mt-1">Redirecting you to log in…</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-display font-black text-3xl mb-2">Set a new password</h1>
            <p className="text-zinc-600 mb-8">Choose something you'll remember. At least 6 characters.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="label">New password</label>
                <input className="input" type="password" required minLength={6} value={newPass} onChange={(e) => setNewPass(e.target.value)} data-testid="reset-new" />
              </div>
              <div>
                <label className="label">Confirm new password</label>
                <input className="input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
              </div>
              <button type="submit" className="btn btn-primary w-full" disabled={loading} data-testid="reset-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
